/**
 * Fitment card and PLP vehicle bar (RAZ-338).
 *
 * One Alpine component drives three surfaces:
 *  - `mode: 'card'` — blocks/rb-fitment-card.liquid, the PDP buy-rail card that tells
 *    the shopper whether this product is listed for their saved vehicle;
 *  - `mode: 'bar'` — blocks/rb-vehicle-bar.liquid, the PLP strip above the grid, which
 *    speaks about the collection it is sitting on and nothing else;
 *  - `mode: 'note'` — blocks/rb-fitment-note.liquid, the one line under add to cart that
 *    says a cover is not matched to the saved vehicle, at the point of decision.
 *
 * It decides nothing about fit. The matching rule lives in fitmentState() in
 * src/js/utils/vehicleResolver.js — collection membership, gated on the fitment's
 * `definitive` flag — and this component only reports what that function returns, so
 * the card, the PLP bar, the finder and the migration's verify step can never
 * disagree about what a selection means.
 *
 * Product identity arrives from Liquid on the host element's data attributes; nothing
 * here fetches the product. Nothing here re-reads the catalogue either: the saved
 * vehicle already carries its destination and its model's collections, captured at
 * save time by buildVehicle().
 *
 * Failure is survivable. A catalogue that will not load leaves `hasError` true and the
 * card still renders from the saved vehicle as it stands, because an unreconciled
 * vehicle is better than a blank buy rail.
 */

import { loadVehicleData } from '../utils/vehicleData.js';
import { destinationUrl, fill, fitmentState } from '../utils/vehicleResolver.js';

/**
 * The `reason` src/js/stores/vehicle.js gives when it drops a vehicle the catalogue no
 * longer holds, as opposed to one the shopper removed or one that timed out. Declared
 * here rather than imported, because importing the store would pull Alpine into this
 * module — the same reason the panel event names above are declared rather than shared.
 */
const CLEARED_BY_CATALOGUE = 'catalogue';

/** Window event every "Change" and "Add your vehicle" affordance dispatches. */
export const VEHICLE_PANEL_TOGGLE_EVENT = 'arc:action:vehicle-panel-toggle';

/** Header panel events this component listens to, so aria-expanded stays honest. */
export const VEHICLE_PANEL_SHOW_EVENT = 'arc:action:vehicle-panel-show';
export const VEHICLE_PANEL_HIDE_EVENT = 'arc:action:vehicle-panel-hide';

/** The surfaces this component drives. Anything else is treated as the PDP card. */
const MODES = new Set(['card', 'bar', 'note']);

/** Product types that fit every vehicle, used when the theme setting is missing. */
export const DEFAULT_EXEMPT_PRODUCT_TYPES = 'Console Lid,Bundle';

/**
 * Default copy for every state, so the component is correct before a merchant has
 * touched a single block setting. Each string is a fill() template: see labels() in
 * the resolver for the tokens.
 */
export const DEFAULT_COPY = {
  fits: 'Fits your {sentence}',
  confirmComplete: 'Made for the {model_series}. Check the variant on this cover.',
  confirmPartial: 'Made for the {model_series}. Add your {next_step} to confirm the fit.',
  otherVariant: 'Made for other {model} variants, not listed for your {series_variant}.',
  noFit: 'Not listed for your {sentence}.',
  prompt: 'Check this fits your vehicle',
  changeLabel: 'Change',
  promptActionLabel: 'Add your vehicle',
  shopLabel: 'Shop covers for your {model}',
  barFits: 'Showing covers guaranteed to fit your {model_series_variant}. Every product below is airbag-safe certified.',
  barFitsCompact: 'Guaranteed to fit your {model_series_variant}. All airbag-safe certified.',
  barConfirm: 'Showing covers made for your {model_series}. Check the variant on each cover.',
  barConfirmCompact: 'Made for your {model_series}. Check the variant on each cover.',
  barModel: "You're browsing all {model} covers.",
  barModelCompact: "All {model} covers.",
  barModelLabel: 'See covers for your {series_variant}',
  note: "Adding a cover that isn't matched to your saved vehicle",
};

/** The two states the add-to-cart note speaks on. Every other state renders nothing. */
const NOTE_STATES = new Set(['other_variant', 'no_fit']);

/** Card state to the tone the block paints it in. */
const TONE_BY_STATE = {
  loading: 'loading',
  prompt: 'prompt',
  fits: 'fits',
  confirm: 'confirm',
  // The bar's "you are browsing the whole model" state is a signpost, not a promise, so
  // it wears the same cautious treatment as confirm rather than a colour of its own.
  model: 'confirm',
  other_variant: 'neutral',
  no_fit: 'neutral',
  hidden: 'hidden',
};

/** Bar state to the copy template it speaks with, wide and narrow. */
const BAR_COPY_KEYS = { fits: 'barFits', confirm: 'barConfirm', model: 'barModel' };
const BAR_COMPACT_KEYS = {
  fits: 'barFitsCompact',
  confirm: 'barConfirmCompact',
  model: 'barModelCompact',
};

/**
 * The one debug warning this component emits, guarded so production consoles stay quiet.
 * @param {string} message
 * @param {*} detail
 */
function warnOnce(message, detail) {
  if (typeof window !== 'undefined' && window.enableDebugMode) { console.warn(message, detail); }
}

/**
 * Parse a JSON data attribute, falling back rather than throwing.
 * A block whose attribute was truncated must still render the prompt state.
 *
 * @param {*} value raw attribute
 * @param {*} fallback
 * @returns {*}
 */
function parseJsonAttribute(value, fallback) {
  if (typeof value !== 'string' || value.trim() === '') return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch (error) {
    warnOnce('fitmentCard: a data attribute was not valid JSON', error);
    return fallback;
  }
}

/**
 * Copy overrides worth applying.
 *
 * A block placed before a copy setting existed hands back null for it, and a merchant
 * can empty a text field. Either would otherwise blank the sentence outright, so only
 * real strings are allowed to displace a default.
 *
 * @param {*} overrides
 * @returns {Object<string, string>}
 */
function usableCopy(overrides) {
  const source = overrides && typeof overrides === 'object' ? overrides : {};
  const usable = {};
  Object.entries(source).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim() !== '') usable[key] = value;
  });
  return usable;
}

/**
 * The merchant's exempt product-type list, as a comparison set.
 * @param {string} [configured] injectable so the rule can be tested without a window
 * @returns {Set<string>}
 */
function exemptTypeSet(configured) {
  let raw = configured;
  if (typeof raw !== 'string') {
    try {
      raw = window.themeSettings?.vehicleExemptProductTypes;
    } catch {
      raw = undefined;
    }
  }
  if (typeof raw !== 'string' || raw.trim() === '') raw = DEFAULT_EXEMPT_PRODUCT_TYPES;
  return new Set(
    raw
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  );
}

/**
 * Is the page narrowed to the saved vehicle's variant filter?
 *
 * A filter destination — a shared collection plus a vehicle_variants filter, such as
 * BYD Sealion 6 Premium — is only guaranteed to fit while that filter is on. The same
 * collection unfiltered, or filtered to another variant as well, shows covers for every
 * variant it serves, so the bar must not promise a fit there. The grid lists products
 * matching ANY label in a filter, so the promise holds only when every label on the page
 * is one the vehicle asked for.
 *
 * Reads both the theme's own `filter-<id>=A,B` form and Shopify's native repeated
 * `<id>=A` form, because a shopper can arrive on either.
 *
 * @param {string} destinationQuery the saved vehicle's dest.query, '' for a plain collection
 * @param {string} search the page's location.search
 * @returns {boolean} true when nothing is required or the page carries only wanted labels
 */
export function isDestinationFilterOn(destinationQuery, search) {
  if (typeof destinationQuery !== 'string' || destinationQuery === '') return true;

  const current = new URLSearchParams(typeof search === 'string' ? search : '');
  const splitLabels = (values) =>
    values.flatMap((value) => value.split(',')).map((label) => label.trim()).filter(Boolean);

  return [...new URLSearchParams(destinationQuery).entries()].every(([key, value]) => {
    const wanted = new Set(splitLabels([value]));
    const nativeKey = key.startsWith('filter-') ? key.slice('filter-'.length) : key;
    const onPage = splitLabels([...current.getAll(key), ...current.getAll(nativeKey)]);
    return onPage.length > 0 && onPage.every((label) => wanted.has(label));
  });
}

/**
 * The page's query string, or '' where there is no window (unit tests, SSR).
 * @returns {string}
 */
function currentSearch() {
  try {
    return typeof window.location?.search === 'string' ? window.location.search : '';
  } catch {
    return '';
  }
}

/**
 * Does this product type fit every vehicle, so no card should ever be shown?
 *
 * The block already leaves the markup out for an exempt type; this is the second
 * gate, reading the same theme setting, so a cached page rendered before the
 * merchant edited the list still hides itself.
 *
 * @param {*} productType the product's custom.cover_product_type, or its type
 * @param {string} [configured] the setting value, read from themeSettings when omitted
 * @returns {boolean}
 */
export function isExemptProductType(productType, configured) {
  const value = typeof productType === 'string' ? productType.trim().toLowerCase() : '';
  if (value === '') return false;
  return exemptTypeSet(configured).has(value);
}

export default {
  name: 'fitmentCard',

  /**
   * @param {Object} [params]
   * @param {'card'|'bar'|'note'} [params.mode] which surface this is (default 'card')
   * @param {Object} [params.copy] copy overrides, merged over DEFAULT_COPY
   * @param {string} [params.panelId] id of the header vehicle panel, for aria-controls
   * @param {string} [params.collection] handle of the collection the bar sits on
   * @param {string} [params.search] the page's query string, read from the window when omitted
   * @returns {Object} Alpine component
   */
  component(params = {}) {
    return {
      mode: MODES.has(params.mode) ? params.mode : 'card',
      copy: { ...DEFAULT_COPY, ...usableCopy(params.copy) },
      panelId: typeof params.panelId === 'string' && params.panelId !== '' ? params.panelId : null,

      // The collection this page IS, written in by rb-vehicle-bar.liquid. The bar has
      // nothing honest to say without it, so a missing handle hides the bar rather than
      // letting it speak for a page it cannot see.
      collection:
        typeof params.collection === 'string' && params.collection !== ''
          ? params.collection
          : null,

      // The page's query string. The collection grid applies and removes filters without
      // a reload, so watchPage() refreshes this whenever the grid reloads or the shopper
      // steps back through history.
      pageSearch: typeof params.search === 'string' ? params.search : currentSearch(),

      // True until the catalogue has settled. The card holds its reserved height while
      // this is true rather than claiming a fit it may have to take back: refresh()
      // can downgrade a saved vehicle whose fitment the content team has since moved.
      isLoading: true,

      // True when the catalogue could not be loaded. The saved vehicle is then used
      // exactly as it was stored, which is still better than showing nothing.
      hasError: false,

      // Set from the host element's data attributes, or by applyProduct() in tests.
      product: { vehicleCollections: [], variantIds: [], productType: '', isExempt: false },

      // Mirrors the header panel, so the trigger's aria-expanded is not a guess.
      isPanelOpen: false,

      // False until the shopper has changed something. A live region is for changes the
      // shopper caused, so the first render — which they were about to read anyway — is
      // silent, and only a save or a clear arms the announcement.
      isAnnouncing: false,

      /** Listener teardown, registered by watchPanel() and watchPage(). */
      panelListeners: [],

      /* ------------------------------------------------------------------ */
      /* Boot                                                               */
      /* ------------------------------------------------------------------ */

      async init() {
        if (this.$el?.dataset) this.readProduct(this.$el.dataset);
        this.watchPanel();
        if (this.mode === 'bar') this.watchPage();

        try {
          const data = await loadVehicleData();
          this.$store?.vehicle?.refresh(data);
        } catch (error) {
          this.hasError = true;
          warnOnce('fitmentCard: the vehicle catalogue is unavailable', error);
        }

        this.isLoading = false;
      },

      destroy() {
        this.panelListeners.forEach(([name, handler]) => window.removeEventListener(name, handler));
        this.panelListeners = [];
      },

      /**
       * Read the product identity Liquid wrote onto the host element.
       * @param {Object} dataset the element's DOMStringMap
       */
      readProduct(dataset) {
        this.applyProduct({
          vehicleCollections: parseJsonAttribute(dataset.vehicleCollections, []),
          variantIds: parseJsonAttribute(dataset.variantIds, []),
          productType: typeof dataset.productType === 'string' ? dataset.productType : '',
        });
      },

      /**
       * Adopt a product identity. This is the test seam: it needs no network, no DOM
       * and no Alpine.
       *
       * @param {Object} product {vehicleCollections, variantIds, productType}
       */
      applyProduct(product) {
        const collections = Array.isArray(product?.vehicleCollections)
          ? product.vehicleCollections.filter((entry) => entry && entry.h)
          : [];
        const productType = typeof product?.productType === 'string' ? product.productType : '';

        this.product = {
          vehicleCollections: collections,
          variantIds: Array.isArray(product?.variantIds) ? product.variantIds : [],
          productType,
          isExempt: isExemptProductType(productType),
        };
      },

      /* ------------------------------------------------------------------ */
      /* The saved vehicle                                                  */
      /* ------------------------------------------------------------------ */

      /** The saved vehicle, or null. Reactive through the Alpine store. */
      get vehicle() {
        return this.$store?.vehicle?.vehicle ?? null;
      },

      /** True when the catalogue no longer holds every fitment the vehicle named. */
      get isStale() {
        return this.$store?.vehicle?.isStale === true;
      },

      /* ------------------------------------------------------------------ */
      /* State                                                              */
      /* ------------------------------------------------------------------ */

      /**
       * Which state this surface is in.
       *
       * The card's answer is fitmentState()'s, with one downgrade of our own: a stale
       * vehicle — one whose fitment has gone from the catalogue — loses its green
       * badge and asks the shopper to confirm instead.
       *
       * Only the card waits for the catalogue. The bar and the note are decided by the
       * saved vehicle alone, which $persist hydrates synchronously from localStorage,
       * so neither reserves a strip of page for a claim it may never make.
       *
       * @returns {'loading'|'hidden'|'prompt'|'fits'|'confirm'|'model'|'other_variant'|'no_fit'}
       */
      get state() {
        if (this.mode === 'bar') return this.barState;

        const resolved = this.productState;
        if (this.mode === 'note') return NOTE_STATES.has(resolved) ? resolved : 'hidden';
        return this.isLoading ? 'loading' : resolved;
      },

      /**
       * What this product is to the saved vehicle, with the stale downgrade applied.
       * @returns {'hidden'|'prompt'|'fits'|'confirm'|'other_variant'|'no_fit'}
       */
      get productState() {
        const resolved = fitmentState(this.product, this.vehicle);
        return resolved === 'fits' && this.isStale ? 'confirm' : resolved;
      },

      /**
       * What the bar may say about THIS collection.
       *
       * The bar sits on a page that already is a vehicle — a collection of covers for
       * one model, generation or variant — and every claim it makes is about that page,
       * never about the vehicle in the abstract. A shopper with a saved HiLux browsing
       * Nissan Patrol GQ covers must not be told the grid in front of them is guaranteed
       * to fit: those are the products of another vehicle entirely.
       *
       *   fits     this page IS the saved vehicle's destination, and that destination
       *            was built for the one variant they chose — for a filter destination,
       *            only while the page is narrowed to that variant's filter
       *   confirm  this page IS their destination, but it serves more than one variant,
       *            their vehicle is still partial, or its variant filter is off
       *   model    a different page of their own model — honest signpost, with a link
       *   hidden   anything else, including a page we cannot identify
       *
       * @returns {'hidden'|'fits'|'confirm'|'model'}
       */
      get barState() {
        const saved = this.vehicle;
        const here = this.collection;
        if (!saved || !here) return 'hidden';

        if (here === saved.dest?.collection) {
          const isConfirmed =
            saved.complete === true &&
            saved.dest?.definitive === true &&
            !this.isStale &&
            isDestinationFilterOn(saved.dest?.query, this.pageSearch);
          return isConfirmed ? 'fits' : 'confirm';
        }

        return this.isModelCollection(here) ? 'model' : 'hidden';
      },

      /**
       * Is this handle one of the saved vehicle's own model's collections?
       * Captured at save time by buildVehicle(), so no catalogue is needed here.
       *
       * @param {string} handle
       * @returns {boolean}
       */
      isModelCollection(handle) {
        const saved = this.vehicle;
        if (!saved) return false;
        const owned = Array.isArray(saved.modelCollections) ? saved.modelCollections : [];
        return owned.includes(handle) || saved.model?.c === handle;
      },

      /** Class suffix the block paints the state in. */
      get tone() {
        return TONE_BY_STATE[this.state] ?? 'neutral';
      },

      /** Is there anything at all to render? */
      get isVisible() {
        return this.state !== 'hidden';
      },

      /**
       * Only a complete vehicle on a definitive destination earns the green badge.
       * The whole point of the `definitive` flag is that an indeterminate match must
       * not be dressed as a confirmed one.
       */
      get shouldShowBadge() {
        return this.state === 'fits';
      },

      /* ------------------------------------------------------------------ */
      /* Copy                                                               */
      /* ------------------------------------------------------------------ */

      /** Which copy template the current state speaks with. */
      get copyKey() {
        if (this.state === 'hidden' || this.state === 'loading') return null;
        if (this.mode === 'note') return 'note';
        if (this.mode === 'bar') return BAR_COPY_KEYS[this.state] ?? null;

        switch (this.state) {
          case 'fits':
            return 'fits';
          case 'confirm':
            return this.vehicle?.complete === true ? 'confirmComplete' : 'confirmPartial';
          case 'other_variant':
            return 'otherVariant';
          case 'no_fit':
            return 'noFit';
          case 'prompt':
            return 'prompt';
          default:
            return null;
        }
      },

      /** The sentence this surface shows, with the vehicle's labels filled in. */
      get message() {
        const key = this.copyKey;
        return key ? fill(this.copy[key], this.vehicle) : '';
      },

      /**
       * The short form, used by the bar below 768px. The card has one message, so it
       * hands back the same string rather than a second setting nobody would fill.
       */
      get messageCompact() {
        if (this.mode !== 'bar') return this.message;
        const key = BAR_COMPACT_KEYS[this.state] ?? null;
        return key ? fill(this.copy[key], this.vehicle) : '';
      },

      /* ------------------------------------------------------------------ */
      /* The one action                                                     */
      /* ------------------------------------------------------------------ */

      /**
       * What the card's single affordance does. `Change` is a link back to the one
       * header selector, never a per-product override, so it opens the panel rather
       * than editing anything here.
       *
       * @returns {'none'|'panel'|'shop'}
       */
      get actionKind() {
        if (this.mode === 'note') return 'none';
        // The bar's one link takes the shopper from the whole model to their own covers.
        if (this.mode === 'bar') return this.state === 'model' ? 'shop' : 'none';
        if (this.state === 'prompt' || this.state === 'fits' || this.state === 'confirm') {
          return 'panel';
        }
        if (this.state === 'other_variant' || this.state === 'no_fit') return 'shop';
        return 'none';
      },

      get hasAction() {
        if (this.actionKind === 'none') return false;
        if (this.actionKind === 'shop') return this.actionHref !== null;
        return true;
      },

      get actionLabel() {
        if (this.mode === 'bar') return fill(this.copy.barModelLabel, this.vehicle);
        if (this.actionKind === 'shop') return fill(this.copy.shopLabel, this.vehicle);
        if (this.state === 'prompt') return this.copy.promptActionLabel;
        return this.copy.changeLabel;
      },

      /** Where "Shop covers for your …" goes: the saved vehicle's own destination. */
      get actionHref() {
        if (this.actionKind !== 'shop') return null;
        return destinationUrl(this.vehicle?.dest ?? null);
      },

      /* ------------------------------------------------------------------ */
      /* Accessibility                                                      */
      /* ------------------------------------------------------------------ */

      /**
       * What the live region says.
       *
       * Empty until the shopper changes something. A live region is for changes the
       * shopper caused, and on arrival this surface is simply part of the page they are
       * reading — announcing it would interrupt them with text they were about to reach.
       * Saving or clearing a vehicle rewrites the card in place with no navigation,
       * which is the change worth speaking, so that is what arms it.
       */
      get statusMessage() {
        if (!this.isAnnouncing) return '';
        if (this.state === 'loading' || this.state === 'hidden') return '';
        return this.hasAction ? `${this.message} ${this.actionLabel}.` : this.message;
      },

      /**
       * Ask the header vehicle panel to open. Both the "Change" link and the prompt's
       * "Add your vehicle" use it, because there is one selector on the page and this
       * card is a way back to it.
       *
       * It does not guess at the outcome. The panel owns its own state and announces
       * every open and close (src/js/components/vehicleChip.js), and watchPanel() below
       * mirrors those two events, so aria-expanded stays true only while a panel really
       * is on screen — including on a page that renders no panel at all.
       */
      openPanel() {
        try {
          window.dispatchEvent(new CustomEvent(VEHICLE_PANEL_TOGGLE_EVENT));
        } catch (error) {
          warnOnce('fitmentCard: the vehicle panel could not be opened', error);
        }
      },

      /**
       * Track the header panel so aria-expanded reflects it rather than guessing.
       * The panel owns opening, focus and Escape; this only mirrors its state.
       */
      watchPanel() {
        const bind = (name, value) => {
          const handler = () => {
            this.isPanelOpen = value;
          };
          window.addEventListener(name, handler);
          this.panelListeners.push([name, handler]);
        };

        bind(VEHICLE_PANEL_SHOW_EVENT, true);
        bind(VEHICLE_PANEL_HIDE_EVENT, false);
        bind('arc:action:vehicleSet:success', false);
        bind('arc:action:vehicleCleared', false);

        // The two changes the shopper made themselves, and the only two worth speaking.
        // A vehicle the catalogue lost is cleared by the store during boot, before the
        // shopper has touched anything, so that clear is deliberately not spoken: the
        // card simply renders its prompt, which is what an arriving shopper reads anyway.
        const arm = (event) => {
          if (event?.detail?.reason === CLEARED_BY_CATALOGUE) return;
          this.isAnnouncing = true;
        };
        ['arc:action:vehicleSet:success', 'arc:action:vehicleCleared'].forEach((name) => {
          window.addEventListener(name, arm);
          this.panelListeners.push([name, arm]);
        });
      },

      /**
       * Keep pageSearch in step with the grid. The search store rewrites the URL with
       * pushState and then reloads the grid, so its load events — and history steps —
       * are the moments a filter can have come on or gone off.
       */
      watchPage() {
        const sync = () => {
          this.pageSearch = currentSearch();
        };
        ['collection:loaded', 'search:filtersCleared', 'popstate'].forEach((name) => {
          window.addEventListener(name, sync);
          this.panelListeners.push([name, sync]);
        });
      },
    };
  },
};
